import { domainCopy } from '../copy';
import type { Language } from '../types/language';
import { isGlyphKey, resolveGlyphKey } from '../types/glyphs';
import type { QuestMissionContent } from '../types/questAdmin';
import { questContentFields, type QuestContentField } from './questContent';
import {
  QUEST_TEXT_ROLES,
  poppinsWidth,
  tileTypeRole,
  wrapLines,
  type QuestLineRole,
  type TextWidth,
} from './questLines';

/**
 * **THE CHECKER** (CONTENT_ENGINE.md §5): one pure function, called in three places — live in the editor as the author
 * types, across the library for the health view, and as the gate on publish. **Errors block publishing and there is
 * no override**; warnings show but do not block. Ported rule for rule from the prototype's `lintMission`
 * (docs/handoff/design/content-engine/engine.js), on the engine's content shape, measured with the one Poppins ruler.
 *
 * **Report, then draft**: where a finding has a mechanical fix (a line break, a mixed row, a number to write as a word,
 * a retired icon name), the issue carries it. Nothing applies itself; a person does, and the review it touched clears.
 */

export type QuestCheckRule =
  | 'spoken-missing'
  | 'beat-count'
  | 'too-long'
  | 'answer-not-tile'
  | 'tile-count'
  | 'mixed-row'
  | 'unknown-icon'
  | 'retired-icon'
  | 'draft-icon'
  | 'no-consequence'
  | 'consequence-digits'
  | 'bb'
  | 'parent-word'
  | 'pronoun'
  | 'unreviewed'
  | 'duplicate'
  | 'same-icon'
  | 'reading-level'
  | 'long-mission'
  | 'line-break';

/** How each rule is named in a list (the prototype's `RULES`), in English; `questCheckRules(language)` for another. */
export const QUEST_CHECK_RULES: Readonly<Record<QuestCheckRule, string>> = domainCopy().questCheck.rules;
export const questCheckRules = (language?: Language): Readonly<Record<QuestCheckRule, string>> =>
  domainCopy(language).questCheck.rules;

/** §5 "Warnings show but don't block". Every other rule is an error. */
export const QUEST_CHECK_WARNINGS: ReadonlySet<QuestCheckRule> = new Set([
  'duplicate',
  'same-icon',
  'reading-level',
  'long-mission',
  'line-break',
]);

export interface QuestIssue {
  /** Stable per rule and field, so an editor can key on it. */
  id: string;
  rule: QuestCheckRule;
  severity: 'error' | 'warning';
  /** The field it is about, in `QuestMissionContent` terms; `['mission']` for the whole mission. */
  path: Array<string | number>;
  /** "Beat 2 · reader", "Question 1 · tile 3" — the labels O13 prints. */
  where: string;
  message: string;
  /** A mechanical fix, drafted: the label for the control, and what it does to the content. */
  fix?: { label: string; apply: (m: QuestMissionContent) => QuestMissionContent };
}

/** The comparison set for the duplicate warning: every other mission's beat prompts and questions. */
export interface QuestLibraryEntry {
  missionId: string;
  label: string;
  texts: Array<{ where: string; text: string; tokens: Set<string> }>;
}

export interface QuestCheckContext {
  /** The mission being checked, so the library comparison skips itself. */
  missionId?: string;
  /** The ruler; the shared Poppins one unless a test passes its own. */
  width?: TextWidth;
  /** Adds the review rule: whether each script's sign-off covers the words as they stand. */
  reviews?: { reader: boolean; prereader: boolean };
  /** Adds the duplicate warning. */
  library?: QuestLibraryEntry[];
  /** Adds the long-mission warning: reader word counts of the adventure's missions. */
  siblings?: number[];
  /**
   * The uploaded icons, by key, and whether each is a draft, live or retired (the icon library). With it, a tile's,
   * the mission's or its badge's `art` may name an uploaded icon — and an icon still in draft is an error, so nothing
   * reaches a child before a Publisher who did not upload it has published it. Without it, only bundled keys draw.
   */
  icons?: Readonly<Record<string, 'draft' | 'live' | 'retired'>>;
  /** The language the messages and the `where` labels are in; English when none is given. */
  language?: Language;
}

// The rules below read the lesson TEXT, which is English: Spanish lessons need their own rules, not a translation of these.
const PARENT_WORDS = /\b(mom|mum|mommy|mummy|dad|daddy|mother|father)\b/i;
const PRONOUNS = /\b(he|him|his|himself|she|her|hers|herself)\b/i;
const CAST = /\b(kai|pip)\b/i;
const MONEY = /\$\s?\d[\d,.]*|\b\d[\d,.]*\s?(?:dollars?|cents?|Bamboo Bucks?)\b/gi;
const STOP = new Set(
  'a an the and or to of is it in on for at be can this that what which you your with as by if so'.split(' '),
);

const wordsOf = (t: string) => t.replace(/\*\*/g, '').match(/[A-Za-z0-9'’-]+/g) ?? [];
const sentencesOf = (t: string) =>
  t
    .split(/(?<=[.!?])\s+|\n+/)
    .map((s) => s.trim())
    .filter(Boolean);

export const questTokens = (t: string): Set<string> =>
  new Set(
    wordsOf(t)
      .map((w) => w.toLowerCase().replace(/['’]s$/, ''))
      .filter((w) => !STOP.has(w)),
  );

function jaccard(a: Set<string>, b: Set<string>): number {
  let n = 0;
  for (const x of a) if (b.has(x)) n++;
  return n / (a.size + b.size - n || 1);
}

export const withoutMoney = (t: string) => t.replace(MONEY, '');

const ONES =
  'zero one two three four five six seven eight nine ten eleven twelve thirteen fourteen fifteen sixteen seventeen eighteen nineteen'.split(
    ' ',
  );
const TENS = ',,twenty,thirty,forty,fifty,sixty,seventy,eighty,ninety'.split(',');

/** A whole number from 0 to 999 as words: "sixty-five", "one hundred and ten". */
export function numberWords(n: number): string {
  if (n < 20) return ONES[n]!;
  if (n < 100) return TENS[Math.floor(n / 10)]! + (n % 10 ? '-' + ONES[n % 10]! : '');
  return ONES[Math.floor(n / 100)]! + ' hundred' + (n % 100 ? ' and ' + numberWords(n % 100) : '');
}

/** Every number that is not money, as words (§5: money amounts may stay as digits). */
export function wordsForNonMoney(t: string): string {
  // the placeholder names each money amount in LETTERS: a digit in it would be the next thing turned into a word
  // (the prototype's did exactly that — "65 Bamboo Bucks" came back as "zero")
  const keep: string[] = [];
  const tag = (i: number) => i.toString(26).replace(/./g, (d) => String.fromCharCode(97 + parseInt(d, 26)));
  const masked = t.replace(MONEY, (m) => {
    keep.push(m);
    return `\u0000${tag(keep.length - 1)}\u0000`;
  });
  return masked
    .replace(/\b\d{1,3}\b/g, (d) => numberWords(Number(d)))
    .replace(/\u0000([a-z]+)\u0000/g, (_, k: string) => keep[parseInt([...k].map((c) => (c.charCodeAt(0) - 97).toString(26)).join(''), 26)]!);
}

/** What a tile draws (§3.3): its own key, a bundled picture its id names, type by choice, or type because nothing fits. */
export function questTileArt(
  tile: { id: string; art?: string | null },
  icons?: QuestCheckContext['icons'],
):
  | { kind: 'icon'; key: string; explicit: boolean; retired: boolean; draft?: boolean }
  | { kind: 'type'; why: 'chosen' | 'unknown-art' | 'no-match' } {
  if (tile.art === null) return { kind: 'type', why: 'chosen' };
  if (tile.art) {
    const key = resolveGlyphKey(tile.art);
    if (key) return { kind: 'icon', key, explicit: true, retired: key !== tile.art || !isGlyphKey(tile.art) };
    // an uploaded icon is only ever named explicitly (CONTENT_ENGINE.md rule 8: an upload never matches a tile id)
    const uploaded = icons?.[tile.art];
    return uploaded
      ? { kind: 'icon', key: tile.art, explicit: true, retired: uploaded === 'retired', draft: uploaded === 'draft' }
      : { kind: 'type', why: 'unknown-art' };
  }
  const key = resolveGlyphKey(tile.id);
  return key ? { kind: 'icon', key, explicit: false, retired: false } : { kind: 'type', why: 'no-match' };
}

function getAt(obj: unknown, path: Array<string | number>): unknown {
  return path.reduce<unknown>(
    (o, k) => (o == null ? o : (o as Record<string | number, unknown>)[k]),
    obj,
  );
}

function setAt<T>(obj: T, path: Array<string | number>, value: unknown): T {
  const copy = (Array.isArray(obj) ? [...obj] : { ...(obj as object) }) as Record<string | number, unknown>;
  const [head, ...rest] = path;
  copy[head!] = rest.length === 0 ? value : setAt((obj as Record<string | number, unknown>)[head!], rest, value);
  return copy as T;
}

/** A field's words live either as a string or as `{ text }` (a script); a fix writes back the same way. */
function setText(m: QuestMissionContent, path: Array<string | number>, next: string): QuestMissionContent {
  const at = getAt(m, path);
  return at && typeof at === 'object' ? setAt(m, [...path, 'text'], next) : setAt(m, path, next);
}

/** The role a field is measured in: a type tile takes its row's step (§3.3). */
function lineRoleOf(f: QuestContentField, m: QuestMissionContent): QuestLineRole | null {
  if (f.role === 'spoken') return null;
  if (f.role !== 'tileCaption') return QUEST_TEXT_ROLES[f.role];
  const q = m.quiz[f.path[1] as number]!;
  const allPictures = q.tiles.length > 0 && q.tiles.every((t) => questTileArt(t).kind === 'icon');
  return allPictures ? QUEST_TEXT_ROLES.tileCaption : tileTypeRole(q.tiles.map((t) => t.caption));
}

/** A script's words, stored as `{ text }` or, in content from before scripts, as the string itself. */
const words = (s: unknown): string =>
  typeof s === 'string' ? s : ((s as { text?: string } | undefined)?.text ?? '');

const SENTENCE_WORDS: Partial<Record<QuestContentField['role'], number>> = { beat: 20, question: 20, spoken: 10 };

/** How many words a reader meets in a mission: the long-mission warning's measure. */
export function questReaderWords(m: QuestMissionContent): number {
  return questContentFields(m)
    .filter((f) => f.role !== 'spoken')
    .reduce((n, f) => n + wordsOf(f.text).length, 0);
}

/** The duplicate warning's comparison set, from the missions of the library. */
export function questLibraryIndex(
  missions: Array<{ id: string; label: string; content: QuestMissionContent }>,
): QuestLibraryEntry[] {
  return missions.map((m) => ({
    missionId: m.id,
    label: m.label,
    texts: questContentFields(m.content)
      .filter((f) => f.role === 'beat' || f.role === 'question')
      .map((f) => ({ where: f.where, text: f.text, tokens: questTokens(f.text) })),
  }));
}

/** Every problem with one mission (§5). */
export function checkMission(m: QuestMissionContent, ctx: QuestCheckContext = {}): QuestIssue[] {
  const width = ctx.width ?? poppinsWidth;
  const c = domainCopy(ctx.language).questCheck;
  const w = domainCopy(ctx.language).questWhere;
  const out: QuestIssue[] = [];
  const add = (
    rule: QuestCheckRule,
    path: Array<string | number>,
    where: string,
    message: string,
    fix?: QuestIssue['fix'],
  ) =>
    out.push({
      id: `${rule}:${path.join('.')}`,
      rule,
      severity: QUEST_CHECK_WARNINGS.has(rule) ? 'warning' : 'error',
      path,
      where,
      message,
      ...(fix ? { fix } : {}),
    });

  // ---- the mission's own picture and its badge (checked against the icon library when it is given)
  if (ctx.icons) {
    const picture = (key: string | undefined, path: Array<string | number>, where: string) => {
      if (!key || resolveGlyphKey(key)) return;
      const status = ctx.icons![key];
      if (!status) add('unknown-icon', path, where, c.notInLibrary(key));
      else if (status === 'draft') add('draft-icon', path, where, c.notPublished(key));
    };
    picture(m.art, ['art'], w.missionPicture);
    picture(m.badge?.art, ['badge', 'art'], w.badge);
  }

  // ---- structure
  const beats = m.beats ?? [];
  if (m.mechanic === 'story' && (beats.length < 2 || beats.length > 3))
    add('beat-count', ['beats'], w.mission, c.beatCount(beats.length));
  beats.forEach((b, i) => {
    if (!words(b.spoken).trim())
      add('spoken-missing', ['beats', i, 'spoken'], w.beatPrereader(i + 1), c.nothingToHear);
    (b.choices ?? []).forEach((ch, j) => {
      if (!words(ch.consequence).trim())
        add(
          'no-consequence',
          ['beats', i, 'choices', j, 'consequence'],
          w.beatChoice(i + 1, ch.key),
          c.noConsequence,
        );
    });
  });
  m.quiz.forEach((q, i) => {
    const n = q.tiles.length;
    const question = w.question(i + 1);
    if (!words(q.spoken).trim())
      add('spoken-missing', ['quiz', i, 'spoken'], w.questionPrereader(i + 1), c.nothingToHear);
    if (n < 2 || n > 4) add('tile-count', ['quiz', i, 'tiles'], question, c.tileCount(n));
    if (!q.tiles.some((t) => t.id === q.answerTileId))
      add('answer-not-tile', ['quiz', i, 'answerTileId'], question, c.answerNotTile);
    const arts = q.tiles.map((t) => questTileArt(t, ctx.icons));
    arts.forEach((a, j) => {
      const t = q.tiles[j]!;
      const where = w.questionTile(i + 1, j + 1);
      if (a.kind === 'type' && a.why === 'unknown-art')
        add('unknown-icon', ['quiz', i, 'tiles', j, 'art'], where, c.notInLibrary(String(t.art)));
      if (a.kind === 'icon' && a.draft)
        add('draft-icon', ['quiz', i, 'tiles', j, 'art'], where, c.notPublished(a.key));
      else if (a.kind === 'icon' && a.retired && ctx.icons?.[a.key] === 'retired')
        add('retired-icon', ['quiz', i, 'tiles', j, 'art'], where, c.retired(a.key));
      else if (a.kind === 'icon' && a.retired)
        add(
          'retired-icon',
          ['quiz', i, 'tiles', j, 'art'],
          where,
          c.retiredName(String(t.art), a.key),
          { label: c.useKey(a.key), apply: (x) => setAt(x, ['quiz', i, 'tiles', j, 'art'], a.key) },
        );
    });
    const pictures = arts.filter((a) => a.kind === 'icon').length;
    if (pictures > 0 && pictures < n) {
      const missing = q.tiles.filter((_, j) => arts[j]!.kind !== 'icon').map((t) => `“${t.caption}”`);
      add(
        'mixed-row',
        ['quiz', i, 'tiles'],
        w.questionTiles(i + 1),
        c.mixedRow(pictures, n, missing.join(', '), missing.length),
        {
          label: c.drawAllAsType,
          apply: (x) =>
            q.tiles.reduce((acc, _t, j) => setAt(acc, ['quiz', i, 'tiles', j, 'art'], null), x),
        },
      );
    }
    const seen = new Map<string, number>();
    for (const a of arts) if (a.kind === 'icon') seen.set(a.key, (seen.get(a.key) ?? 0) + 1);
    for (const [key, count] of seen)
      if (count > 1) add('same-icon', ['quiz', i, 'tiles'], w.questionTiles(i + 1), c.sameIcon(key));
  });

  // ---- strings
  for (const f of questContentFields(m, ctx.language)) {
    const text = f.text;
    if (!text.trim()) continue;
    if (/\bBB\b/.test(text)) add('bb', f.path, f.where, c.bb);
    const parent = text.match(PARENT_WORDS);
    if (parent) add('parent-word', f.path, f.where, c.parentWord(parent[0]));
    for (const s of sentencesOf(text)) {
      const p = s.match(PRONOUNS);
      if (p && !CAST.test(s)) {
        add('pronoun', f.path, f.where, c.pronoun(p[0]));
        break;
      }
    }
    if (f.role === 'consequence' && /\d/.test(withoutMoney(text)))
      add('consequence-digits', f.path, f.where, c.digits, {
        label: c.digitsFix,
        apply: (x) => setText(x, f.path, wordsForNonMoney(text)),
      });
    if (text.includes('\n'))
      add('line-break', f.path, f.where, c.lineBreak, {
        label: c.lineBreakFix,
        apply: (x) => setText(x, f.path, text.replace(/\s*\n+\s*/g, ' ')),
      });
    const limit = SENTENCE_WORDS[f.role];
    if (limit) {
      const long = sentencesOf(text)
        .map((s) => wordsOf(s).length)
        .filter((n) => n > limit);
      if (long.length)
        add(
          'reading-level',
          f.path,
          f.where,
          c.readingLevel(long[0]!, f.role === 'spoken', limit),
        );
    }
    const role = lineRoleOf(f, m);
    if (role) {
      const lines = wrapLines(text, role, width).length;
      if (lines > role.lines)
        add('too-long', f.path, f.where, c.tooLong(lines, role.lines));
    }
  }

  // ---- reviews
  if (ctx.reviews) {
    if (!ctx.reviews.reader) add('unreviewed', ['review', 'reader'], w.readerScript, c.needsReview);
    if (!ctx.reviews.prereader) add('unreviewed', ['review', 'prereader'], w.prereaderScript, c.needsReview);
  }

  // ---- the library
  if (ctx.library)
    for (const f of questContentFields(m, ctx.language).filter((x) => x.role === 'beat' || x.role === 'question')) {
      const mine = questTokens(f.text);
      if (mine.size < 4) continue;
      for (const other of ctx.library) {
        if (other.missionId === ctx.missionId) continue;
        const hit = other.texts.find((o) => o.tokens.size >= 4 && jaccard(mine, o.tokens) >= 0.6);
        if (hit) {
          add(
            'duplicate',
            f.path,
            f.where,
            c.duplicate(other.label, hit.where.toLocaleLowerCase(domainCopy(ctx.language).time.upperLocale), hit.text),
          );
          break;
        }
      }
    }
  if (ctx.siblings && ctx.siblings.length > 1) {
    const mine = questReaderWords(m);
    const sorted = [...ctx.siblings].sort((a, b) => a - b);
    const median = sorted[Math.floor(sorted.length / 2)]!;
    if (mine > median * 1.5 && mine - median >= 20)
      add('long-mission', ['mission'], w.mission, c.longMission(mine, median));
  }
  return out;
}

export const questErrors = (issues: readonly QuestIssue[]) => issues.filter((i) => i.severity === 'error');

// ---- sign-offs bound to the words they approved -----------------------------------------------------------------

/**
 * **What a script's sign-off covers** (§4: "The reader and pre-reader scripts each carry a reviewer signature … on the
 * version being published"; §5: "Editing a draft clears the review of the script it touched"). A reader reviews every
 * word a reader is shown — beats, choices, consequences, questions, tiles and their pictures, feedback; a pre-reader
 * reviewer everything a pre-reader hears or is shown — the spoken lines, and the choices and tiles both bands draw.
 * The digest of those words is stored with the sign-off, and the sign-off counts only while they are unchanged.
 */
export function questScriptDigest(m: QuestMissionContent, band: 'reader' | 'prereader'): string {
  const fields = questContentFields(m)
    .filter((f) =>
      band === 'reader' ? f.role !== 'spoken' : f.role !== 'beat' && f.role !== 'question',
    )
    .map((f) => `${f.path.join('.')}=${f.text}`);
  const tiles = m.quiz.flatMap((q, i) =>
    q.tiles.map((t, j) => `quiz.${i}.tiles.${j}=${t.id}|${t.art === null ? 'null' : (t.art ?? '')}|${q.answerTileId}`),
  );
  const scripts = m.scripts?.[band] ? [`scripts.${band}=${m.scripts[band]}`] : [];
  return fnv1a([...fields, ...tiles, ...scripts, `title=${m.title}`].join('\n'));
}

/** A short, stable digest (FNV-1a, 52 bits) — an equality check on words, not a secret. */
function fnv1a(s: string): string {
  let h1 = 0x811c9dc5;
  let h2 = 0x01000193;
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 0x01000193) >>> 0;
    h2 = Math.imul(h2 ^ (c + i), 0x01000193) >>> 0;
  }
  return h1.toString(16).padStart(8, '0') + (h2 & 0xfffff).toString(16).padStart(5, '0');
}
